import type { Tokens } from '../augmentation'
import { spacing } from '../spacing'
import { getButtonPalette } from './helpers/get-button-palette-color'
import { type ComponentCreation, type ComponentStyleProps } from './types'

type StyleProps = ComponentStyleProps<'MuiButton'>

export const createMuiButton = ({ palette, misc }: Pick<Tokens, 'palette' | 'misc'>) => {
  const denseSize = {
    '&.DhlButton-dense': {
      height: spacing(2.5),
      padding: 0,
      minWidth: 'unset',
      backgroundColor: 'unset',
      '&:hover': {
        opacity: 0.8,
      },
      ...misc.createFocusVisibleState({
        outlineOffset: misc.borderSize(1),
      }),
    },
  }

  return {
    MuiButton: {
      defaultProps: {
        variant: 'text',
        size: 'medium',
        color: 'primary',
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          '&.DhlButton-notEmphasized': {
            fontWeight: 'normal',
          },
          borderRadius: misc.borderRadius(),
          lineHeight: 'inherit',

          '&:hover': {
            boxShadow: 'none',
          },
          '&:active': {
            opacity: 0.8, // set an opacity on the entire Button!
          },

          '&.DhlButton-square': {
            borderRadius: 0,
          },

          variants: [
            {
              props: { color: 'inherit' },
              style: {
                backgroundColor: 'transparent',
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.grey['400'],
                }),
                ...misc.createFocusVisibleState({
                  outlineStyle: 'solid',
                  outlineWidth: misc.borderSize(3),
                  outlineOffset: misc.borderSize(-3),
                  boxShadow: 'none',
                }),
                '&:not(:focus-visible):not(:disabled):active': {
                  opacity: 0.8,
                },
              },
            },
            {
              props: { color: 'inherit', variant: 'contained' },
              style: {
                backgroundColor: palette.grey['300'],
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.grey['400'],
                }),
              },
            },
            {
              props: { color: 'inherit', variant: 'outlined' },
              style: {
                backgroundColor: 'transparent',
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.grey['200'],
                }),
                ...misc.createFocusVisibleState({
                  backgroundColor: palette.grey['300'],
                }),
              },
            },
            {
              props: { color: 'inherit', variant: 'text' },
              style: {
                backgroundColor: 'transparent',
                ...misc.createHoverOrActiveState({
                  backgroundColor: palette.dark.states.hover,
                }),
              },
            },
            {
              props: { variant: 'contained' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      ...(color === 'alternative'
                        ? {
                            backgroundColor: palette.alternative.main,
                            ...misc.createHoverOrActiveState({
                              backgroundColor: palette.alternative.states.hover,
                            }),
                          }
                        : {}),
                      '&:disabled': {
                        color: palette.action.disabled,
                        backgroundColor: palette.action.disabledBackground,
                        borderWidth: misc.borderSize(2),
                      },
                      ...misc.createFocusVisibleState({
                        outlineStyle: 'solid',
                        outlineWidth: misc.borderSize(3),
                        outlineOffset: misc.borderSize(-3),
                        boxShadow: 'none',
                        outlineColor: currentPallet.states.focus,
                      }),
                      ...misc.createHoverOrActiveState({
                        backgroundColor:
                          !!color && ['dark', 'light'].includes(color)
                            ? currentPallet.states.hover
                            : currentPallet.dark,
                        ...(color === 'dark' ? { color: currentPallet.dark } : {}),
                      }),
                    }
                  : {}
              },
            },
            {
              props: { variant: 'outlined' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      ...(color === 'alternative'
                        ? {
                            color: palette.alternative.contrastText,
                            ...misc.createHoverOrActiveState({
                              backgroundColor: palette.alternative.states.hover,
                            }),
                          }
                        : {
                            color: color === 'light' ? currentPallet.main : currentPallet.notContained,
                          }),
                      borderWidth: misc.borderSize(2),
                      borderStyle: 'solid',
                      '&:disabled': {
                        color: palette.action.disabled,
                        borderWidth: misc.borderSize(2),
                      },
                      ...misc.createFocusVisibleState({
                        outlineStyle: 'solid',
                        outlineWidth: misc.borderSize(3),
                        outlineOffset: misc.borderSize(-3),
                        boxShadow: 'none',
                        outlineColor: currentPallet.states.focus,
                      }),
                      '&:not(:focus-visible):not(:disabled)': {
                        borderColor: currentPallet.main,
                      },
                      ...misc.createHoverOrActiveState({
                        backgroundColor: currentPallet.states.hover,
                      }),
                    }
                  : {
                      borderWidth: misc.borderSize(2),
                    }
              },
            },
            {
              props: { variant: 'text' },
              style: ({ color, theme }: StyleProps) => {
                const currentPallet = getButtonPalette(theme?.palette ?? palette, color)
                return currentPallet
                  ? {
                      border: 0,
                      ...(color === 'alternative'
                        ? {
                            color: palette.alternative.contrastText,
                            ...misc.createHoverOrActiveState({
                              backgroundColor: palette.alternative.states.hover,
                              color: '',
                              '&.DhlButton-dense': {
                                backgroundColor: 'transparent',
                                color: palette.alternative.dark,
                              },
                            }),
                          }
                        : {
                            color: color === 'light' ? currentPallet.main : currentPallet.notContained,
                            ...misc.createHoverOrActiveState({
                              backgroundColor: currentPallet.light,
                              // when dense, no background
                              '&.DhlButton-dense': {
                                backgroundColor: 'transparent',
                                color: currentPallet.dark,
                              },
                            }),
                          }),
                      ...misc.createFocusVisibleState({
                        outlineStyle: 'solid',
                        outlineWidth: misc.borderSize(3),
                        outlineOffset: misc.borderSize(-3),
                        boxShadow: 'none',
                        outlineColor: currentPallet.states.focus,
                      }),
                    }
                  : {}
              },
            },
            {
              props: { align: 'start' },
              style: {
                justifyContent: 'flex-start',
              },
            },
          ],
        },
        sizeSmall: {
          height: spacing(5),
          padding: spacing(1.25, 2),
          fontSize: spacing(1.75),
          '& .MuiButton-startIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          '& .MuiButton-endIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          '& .MuiCircularProgress-root': {
            height: `${spacing(1.5)} !important`,
            width: `${spacing(1.5)} !important`,
          },
          ...denseSize,
        },
        sizeMedium: {
          height: spacing(6),
          padding: spacing(1.75, 2.75),
          fontSize: spacing(2),
          '& .MuiButton-startIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          '& .MuiButton-endIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          ...denseSize,
        },
        sizeLarge: {
          height: spacing(7),
          padding: spacing(2.25, 3.5),
          fontSize: spacing(2),
          '& .MuiButton-startIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          '& .MuiButton-endIcon > *:nth-of-type(1)': {
            fontSize: spacing(3),
          },
          ...denseSize,
        },
      },
    },
  } satisfies ComponentCreation
}
